import { useEffect, useMemo, useRef, useState } from "react";

const MOBILE_QUERY = "(max-width: 640px)";

const getMoodOptions = (lang) => [
  {
    index: 0,
    value: "All",
    label: lang === "en" ? "All moods" : "Svi moodovi",
  },
  { index: 1, value: "clean", label: "Clean Everyday" },
  { index: 2, value: "summer", label: "Summer Heat" },
  { index: 3, value: "date", label: "Date Night" },
  { index: 4, value: "rich", label: "Rich & Addictive" },
  { index: 5, value: "soft", label: "Soft Luxury" },
  { index: 6, value: "signature", label: "Signature Energy" },
];

const withActiveMood = (options = [], activeValue = "All") =>
  options.map((option, index) => ({
    ...option,
    index,
    active: option.value === activeValue,
  }));

function getCategoryIcon(label = "") {
  const value = label.toLowerCase();
  if (value.includes("arabian") || value.includes("araps")) return "☾";
  if (value.includes("designer") || value.includes("dizajn")) return "◈";
  if (value.includes("niche")) return "✦";
  return "";
}

function getCategoryTone(label = "") {
  const value = label.toLowerCase();
  if (value.includes("arabian") || value.includes("araps")) return "arabian";
  if (value.includes("designer") || value.includes("dizajn")) return "designer";
  if (value.includes("niche")) return "niche";
  return "neutral";
}

const getInitialMobileMatch = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia(MOBILE_QUERY).matches;
};

export default function MobileShopV2({
  lang = "sr",
  scentMood = "All",
  onScentMoodChange,
  searchTerm = "",
  onSearchTermChange,
  category = "All",
  categoryOptions = [],
  onCategoryChange,
  season = "All",
  seasonOptions = [],
  onSeasonChange,
  sortBy = "featured",
  sortOptions = [],
  onSortChange,
}) {
  const [isMobile, setIsMobile] = useState(getInitialMobileMatch);
  const [panel, setPanel] = useState(null);
  const menuRef = useRef(null);

  const moodOptions = useMemo(() => getMoodOptions(lang), [lang]);

  const copy = useMemo(
    () =>
      lang === "en"
        ? {
            mood: "Mood",
            moodDefault: "All moods",
            search: "Search",
            searchHint: "Brand or fragrance",
            filter: "Filter",
            filterDefault: "All filters",
            sort: "Sort",
            category: "Category",
            season: "Season",
            reset: "Reset filters",
            clear: "Clear",
            way: "THE PLAYNICE WAY",
            wayNote: "Less risk. More certainty. A better decision.",
          }
        : {
            mood: "Osećaj",
            moodDefault: "Svi osećaji",
            search: "Pretraga",
            searchHint: "Brend ili parfem",
            filter: "Filter",
            filterDefault: "Svi filteri",
            sort: "Sort",
            category: "Kategorija",
            season: "Sezona",
            reset: "Poništi filtere",
            clear: "Obriši",
            way: "PLAYNICE PRISTUP",
            wayNote: "Manje rizika. Više sigurnosti. Bolja odluka.",
          },
    [lang]
  );

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const syncMobile = () => setIsMobile(media.matches);

    media.addEventListener?.("change", syncMobile);

    return () => {
      media.removeEventListener?.("change", syncMobile);
    };
  }, []);

  useEffect(() => {
    if (!panel) return;

    /*
      Close the mobile shop popover after the outside click has completed.

      Using pointerdown here used to mutate the filter UI before the browser
      resolved the following click. On mobile, especially while the search
      input/keyboard was active, the viewport could reflow between pointerdown
      and click and the release would occasionally land on the Filter or Sort
      capsule instead of the product card underneath ("click-through").
    */
    const onDocumentClick = (event) => {
      if (!menuRef.current?.contains(event.target)) setPanel(null);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setPanel(null);
    };

    document.addEventListener("click", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [panel]);

  const openPanel = (name) => {
    if (panel === name) {
      setPanel(null);
      return;
    }
    setPanel(name);
  };

  const applySort = (index) => {
    const option = sortOptions[index];
    if (!option) return;
    onSortChange?.(option.value);
    setPanel(null);
  };

  const applyCategory = (index) => {
    const option = categoryOptions[index];
    if (!option) return;
    onCategoryChange?.(option.value);
  };

  const applySeason = (index) => {
    const option = seasonOptions[index];
    if (!option) return;
    onSeasonChange?.(option.value);
  };

  const applyMood = (index) => {
    const option = moodOptions[index];
    if (!option) return;

    onScentMoodChange?.(option.value);
    setPanel(null);
  };

  const resetFilters = () => {
    onCategoryChange?.("All");
    onSeasonChange?.("All");
    onScentMoodChange?.("All");
    onSearchTermChange?.("");
    onSortChange?.("featured");
  };

  const activeMoodOption = moodOptions.find((option) => option.value === scentMood);
  const activeCategory = categoryOptions.find((option) => option.value === category);
  const activeSeason = seasonOptions.find((option) => option.value === season);
  const activeSort = sortOptions.find((option) => option.value === sortBy);

  const filterCount = [category !== "All", season !== "All"].filter(Boolean).length;

  if (!isMobile) return null;

  const capsuleValue = (name) => {
    if (name === "mood") return activeMoodOption?.label || copy.moodDefault;
    if (name === "search") return searchTerm ? `“${searchTerm}”` : copy.searchHint;
    if (name === "filter") {
      return filterCount ? `${filterCount} ${lang === "en" ? "active" : "aktivna"}` : copy.filterDefault;
    }
    return activeSort?.label || copy.sort;
  };

  const controls = [
    { name: "mood", label: copy.mood, icon: "◌" },
    { name: "search", label: copy.search, icon: "⌕" },
    { name: "filter", label: copy.filter, icon: "≡" },
    { name: "sort", label: copy.sort, icon: "↕" },
  ];

  const menu = (
    <section className="mobile-shop-menu" ref={menuRef} aria-label="Shop menu">
      <div className="mobile-shop-wayline">
        <strong>{copy.way}</strong>
        <span>{copy.wayNote}</span>
      </div>

      <div className="mobile-shop-board">
        <div className="mobile-shop-capsules">
          {controls.map(({ name, label, icon }) => (
            <button
              key={name}
              type="button"
              className={`mobile-shop-capsule ${panel === name ? "active" : ""}`}
              aria-label={`${label}: ${capsuleValue(name)}`}
              aria-expanded={panel === name}
              onClick={() => openPanel(name)}
            >
              <span className="mobile-shop-capsule-icon" aria-hidden="true">{icon}</span>
              <strong>{capsuleValue(name)}</strong>
              <span className="mobile-shop-capsule-arrow" aria-hidden="true">⌄</span>
            </button>
          ))}
        </div>

        {panel && (
          <div className={`mobile-shop-popover mobile-shop-popover-${panel}`}>
            {panel === "mood" && (
              <SelectionList
                options={withActiveMood(moodOptions, scentMood)}
                onChoose={applyMood}
              />
            )}

            {panel === "search" && (
              <div className="mobile-shop-search-panel">
                <div>
                  <span aria-hidden="true">⌕</span>
                  <input
                    id="mobile-shop-search-proxy"
                    aria-label={copy.searchHint}
                    autoFocus
                    type="search"
                    value={searchTerm}
                    placeholder={copy.searchHint}
                    onChange={(event) => {
                      onSearchTermChange?.(event.target.value);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") setPanel(null);
                    }}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        onSearchTermChange?.("");
                      }}
                    >
                      {copy.clear}
                    </button>
                  )}
                </div>
              </div>
            )}

            {panel === "filter" && (
              <div className="mobile-shop-filter-panel">
                <SelectionSection
                  title={copy.category}
                  options={withActiveMood(categoryOptions, category)}
                  onChoose={applyCategory}
                  kind="category"
                />
                <SelectionSection
                  title={copy.season}
                  options={withActiveMood(seasonOptions, season)}
                  onChoose={applySeason}
                />
                <button type="button" className="mobile-shop-reset-link" onClick={resetFilters}>
                  {copy.reset}
                </button>
              </div>
            )}

            {panel === "sort" && (
              <SelectionList
                options={withActiveMood(sortOptions, sortBy)}
                onChoose={applySort}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );

  return menu;
}

function SelectionSection({ title, options, onChoose, kind }) {
  if (!options?.length) return null;
  return (
    <div className="mobile-shop-selection-section">
      <h4>{title}</h4>
      <SelectionList options={options} onChoose={onChoose} kind={kind} />
    </div>
  );
}

function SelectionList({ options, onChoose, kind }) {
  if (!options?.length) return null;
  return (
    <div className="mobile-shop-selection-list">
      {options.map((option) => {
        const categoryIcon = kind === "category" ? getCategoryIcon(option.label) : "";
        const categoryTone = kind === "category" ? getCategoryTone(option.label) : "neutral";
        return (
          <button
            type="button"
            key={`${option.index}-${option.label}`}
            className={option.active ? "active" : ""}
            onClick={() => onChoose(option.index)}
          >
            <span className="mobile-shop-option-label">
              {categoryIcon && (
                <b
                  className={`mobile-shop-option-icon mobile-shop-option-icon-${categoryTone}`}
                  aria-hidden="true"
                >
                  {categoryIcon}
                </b>
              )}
              {option.label}
            </span>
            <i aria-hidden="true">{option.active ? "●" : "○"}</i>
          </button>
        );
      })}
    </div>
  );
}
